import React, { useEffect, useState } from "react";
import ViewTable from "../../../Components/Common/ViewTable";
import { get, post, put, del, api } from "../../../utils/api";
import Cards from "../../../Components/Common/Cards";
import FilterModal from "../../../Components/Common/FilterModal";
import Form from "./Form";
import ActionButtons from "../../../Components/Common/ActionButtons";
import ViewData from "./ViewData.jsx";
import BulkUploadModal from "../../../Components/Common/BulkUploadModal";
import BulkUploadSkippedDialog from "../../../Components/Common/BulkUploadSkippedDialog";
import BulkUploadReview from "./BulkUploadReview";
import {
  Alert,
  Snackbar,
} from "@mui/material";
import { useUser } from "../../../context/AuthContext.js";
import DeleteAlertDialog from "../../../Components/Masters/DeleteAlertDialog.js";
import {
  shouldShowFacilityColumn,
  withFacilityViewTableHeader,
  mapTableDataWithFacility,
} from "../../../utils/facilityColumn";
import { wasteTypesArray } from "../../../utils/constant";

const MATERIALS_BULK_UPLOAD_OPTIONS = wasteTypesArray.join(", ");
const materialsAddedDuringProcessingColumn = (index) =>
  `Materials Added During Processing ${index} (${MATERIALS_BULK_UPLOAD_OPTIONS})`;

const PROCESSING_SKIPPED_DOWNLOAD_COLUMNS = [
  "Product ID / SKU",
  "Customer/Vendor Name",
  "Sold Product Date (DD/MM/YYYY)",
  "Quantity Sold (Value)",
  "Quantity Sold Unit (Pieces/MT/KL)",
  "Electricity Consumption (KWH)",
  "Fuel Name 1 (Natural gas, Coal, Petroleum/Gasoline, Diesel, Fuel oil, LPG, Propane)",
  "Fuel Quantity 1",
  "Fuel Unit 1 (Natural gas: M3, Coal: Tonnes, Kg, Petroleum/Gasoline: Liters, Diesel: Liters, Fuel oil: Liters, LPG: Liters,Tonnes, Propane: Liters, Tonnes)",
  "Fuel Name 2 (Natural gas, Coal, Petroleum/Gasoline, Diesel, Fuel oil, LPG, Propane)",
  "Fuel Quantity 2",
  "Fuel Unit 2 (Natural gas: M3, Coal: Tonnes, Kg, Petroleum/Gasoline: Liters, Diesel: Liters, Fuel oil: Liters, LPG: Liters,Tonnes, Propane: Liters, Tonnes)",
  materialsAddedDuringProcessingColumn(1),
  "Material Quantity 1",
  materialsAddedDuringProcessingColumn(2),
  "Material Quantity 2",
  "Remark (optional)",
];

/** `cardData.totalSoldUnits` → e.g. `6005 MT | 9 KL` — empty array → `""`. */
function formatTotalSoldUnitsSummary(items) {
  if (!Array.isArray(items) || items.length === 0) return "";
  return items
    .map((row) => {
      const qty = row?.totalQuantitySold;
      const unit = String(row?.unit ?? "").trim();
      const hasQty = qty !== undefined && qty !== null && String(qty).trim() !== "";
      if (!hasQty && !unit) return "";
      const q = hasQty ? String(qty).trim() : "";
      return `${q} ${unit}`.replace(/\s+/g, " ").trim();
    })
    .filter(Boolean)
    .join(" | ");
}

const hasDisplayValue = (value) =>
  value !== undefined &&
  value !== null &&
  String(value).trim() !== "" &&
  Number(value) !== 0;

/** `cardData.totalFuel` → e.g. [{ M3: 2, Tonnes: 5 }] → "2 M3 | 5 Tonnes". */
function formatTotalFuel(items) {
  if (!Array.isArray(items) || items.length === 0) return "";
  const unitOrder = ["M3", "Tonnes"];
  return items
    .flatMap((row) =>
      unitOrder
        .filter((unit) => hasDisplayValue(row?.[unit]))
        .map((unit) => `${row[unit]} ${unit}`)
    )
    .filter(Boolean)
    .join(" \u00A0 | \u00A0 ");
}

/** Legacy `cardData.fuelConsumptionSummary` → e.g. `8.092 M3 | 1.021 Tonnes`. */
function formatFuelConsumptionSummary(items) {
  if (!Array.isArray(items) || items.length === 0) return "";
  return items
    .map((row) => {
      const fuel = row?.totalFuelConsumption;
      const unit = String(row?.unit ?? "").trim();
      if (!hasDisplayValue(fuel) || !unit) return "";
      const f = String(fuel).trim();
      return `${f} ${unit}`.replace(/\s+/g, " ").trim();
    })
    .filter(Boolean)
    .join(" \u00A0 | \u00A0 ");
}

const ProcessingOfSoldProducts = () => {
  const { user } = useUser();
  const showFacilityColumn = shouldShowFacilityColumn(user);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [selectedValue, setSelectedValue] = useState(null);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [bulkUploadTable, setBulkUploadTable] = useState(false);
  const [formDataBulkUpload, setFormDataBulkUpload] = useState([]);
  const [matchData, setMatchData] = useState([]);
  const [skippedUploadDialog, setSkippedUploadDialog] = useState({
    open: false,
    skippedEntries: [],
    inserted: 0,
    skipped: 0,
  });
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletedItemId, setDeletedItemId] = useState("");
  const [excelData, setExcelData] = useState(null);
  const [filters, setFilters] = useState({
    productName: [],
    productCategory: [],
    vendorName: [],
    locationOfProcessing: [],
    soldProductDate: [],
  });

  const [cardData, setCardData] = useState({
    totalProcessings: 0,
    topCategory: [],
    topVendor: null,
    totalQuantitySold: 0,
    electricityConsumption: 0,
    totalSoldUnits: [],
    totalFuel: [],
    fuelConsumptionSummary: [],
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  /** Fluid column weights — table fills width with no horizontal scroll (tableLayoutAuto). */
  const headers = withFacilityViewTableHeader(
    [
      { key: "createdAt", label: "Date", sortable: true, fluidWeight: 1.15 },
      {
        key: "soldProductDate",
        label: "Sold Product Date",
        sortable: true,
        fluidWeight: 1.25,
      },
      { key: "productName", label: "Product Name", sortable: true, fluidWeight: 1.1 },
      {
        key: "vendorName",
        label: "Customer/Vendor Name",
        sortable: true,
        fluidWeight: 1.45,
      },
      {
        key: "quantitySoldDisplay",
        label: "Quantity Sold",
        sortable: true,
        fluidWeight: 1.1,
      },
      {
        key: "energyDisplay",
        label: "Electricity (KWH)",
        sortable: true,
        fluidWeight: 0.75,
      },
    ],
    showFacilityColumn,
    { fluidWeight: 1.1 }
  );

  const inputData = [
    { fieldName: "soldProductDate", title: "Sold Product Date" },
    { fieldName: "productId", title: "Product ID" },
    { fieldName: "productName", title: "Product Name" },
    { fieldName: "productCategory", title: "Product Category" },
    { fieldName: "vendorName", title: "Customer/Vendor Name" },
    { fieldName: "quantitySold", title: "Quantity Sold" },
    { fieldName: "locationOfProcessing", title: "Location of Processing" },
    // { fieldName: "processingMethodUsed", title: "Processing Method Used" },
    { fieldName: "electricityConsumption", title: "Electricity Consumption (KWH)" },
    { fieldName: "fuelDisplay", title: "Fuel Consumption" },
    {
      fieldName: "materialsAddedDuringProcessing",
      title: "Materials Added During Processing (MT)",
    },
    { fieldName: "remark", title: "Remark (optional)" },
  ];


  const totalQuantitySoldDescription =
    formatTotalSoldUnitsSummary(cardData?.totalSoldUnits) ||
    (cardData?.totalQuantitySold != null &&
      String(cardData.totalQuantitySold).trim() !== "" &&
      Number(cardData.totalQuantitySold) !== 0
      ? String(cardData.totalQuantitySold)
      : "");

  const totalFuelConsumptionDescription =
    formatTotalFuel(cardData?.totalFuel) ||
    formatFuelConsumptionSummary(cardData?.fuelConsumptionSummary);

  let bannerData = [
    {
      title: "Total Quantity sold",
      description: totalQuantitySoldDescription,
    },
    {
      title: "Total energy consumption in the Process (Mwh)",
      description: `${cardData?.electricityConsumption ?? 0}`,
    },
    {
      title: "Total fuel consumption in the Process",
      description: totalFuelConsumptionDescription,
    },
  ];

  const filterData = [
    { title: "Date", type: "date", key: "createdAt", value: [] },
    {
      title: "Sold product date",
      type: "date",
      key: "soldProductDate",
      value: filters?.soldProductDate,
    },
    {
      title: "Product Name",
      type: "option",
      key: "productName",
      value: filters?.productName,
    },
    // {
    //   title: "Product Category",
    //   type: "option",
    //   key: "productCategory",
    //   value: filters?.productCategory,
    // },

    {
      title: "Vendor Name",
      type: "option",
      key: "vendorName",
      value: filters?.vendorName,
    },
    // {
    //   title: "Location of Processing",
    //   type: "option",
    //   key: "locationOfProcessing",
    //   value: filters?.locationOfProcessing || [],
    // },
  ];

  const fetchExcelData = async () => {
    await get("/master/customerMasters/products-with-customers")
      .then((res) => {
        let data = res.data.data;
        const arr = data.flatMap((el) =>
          (el.customers?.length ? el.customers : [{}]).map((customer) => ({
            ...PROCESSING_SKIPPED_DOWNLOAD_COLUMNS.reduce((row, key) => {
              row[key] = "";
              return row;
            }, {}),
            "Product ID / SKU": el?.productId || "",
            "Customer/Vendor Name": customer?.customerVendorName || "",
          }))
        );

        setExcelData(arr);
      })
      .catch((err) => {
        console.log(err);
      });

  };

  useEffect(() => {
    fetchExcelData();
  }, []);

  const fetchData = async () => {
    const src = filterObj || {};
    const params = {
      page: String(currentPage),
      limit: "10",
    };
    if (sortConfig.key) params.sortField = sortConfig.key;
    if (sortConfig.direction) params.sortOrder = sortConfig.direction;

    if (src.createdAt?.start) {
      params.startDate = src.createdAt.start;
      params.endDate = src.createdAt.end
        ? src.createdAt.end
        : new Date().toISOString().slice(0, 10);
    }

    if (src.soldProductDate?.start) {
      params.soldStartDate = src.soldProductDate.start;
    }
    if (src.soldProductDate?.end) {
      params.soldEndDate = src.soldProductDate.end;
    }

    Object.keys(src).forEach((key) => {
      if (key === "createdAt" || key === "soldProductDate") return;
      const val = src[key];
      if (Array.isArray(val) && val.length > 0) {
        params[key] = val.join(",");
      } else if (val != null && typeof val !== "object" && val !== "") {
        params[key] = String(val);
      }
    });

    const queryParams = new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(
          ([, v]) => v !== undefined && v !== null && String(v).trim() !== ""
        )
      )
    ).toString();

    await get(`/scope3/processingOfSoldProducts/get?${queryParams}`)
      .then((res) => {
        let arr = res?.data?.data?.map((item) => {
          const originalCreatedDate = item?.createdAt;
          const formattedCreatedDate = originalCreatedDate
            ? new Date(originalCreatedDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            : "-";

          const quantitySoldValue = item?.quantitySold?.value
            ? `${item.quantitySold.value} ${item.quantitySold.unit || "KG"}`
            : "-";

          const electricityVal =
            item?.electricityConsumption ?? item?.energyConsumptionForProcessing;
          const energyConsumptionValue =
            electricityVal !== undefined && electricityVal !== null && electricityVal !== ""
              ? `${electricityVal} KWH`
              : "-";

          const fuelEntries = Array.isArray(item?.fuelEntries) ? item.fuelEntries : [];
          const fuelDisplay =
            fuelEntries.length > 0
              ? fuelEntries
                .map((f) => {
                  const qty = f?.fuelConsumption ?? f?.quantity ?? "";
                  const unit = f?.fuelConsumptionUnit || f?.unit || "";
                  const name = f?.fuelType || f?.fuelName || "";
                  return qty ? `${name} ${qty} ${unit}`.trim() : name;
                })
                .filter(Boolean)
                .join(", ")
              : item?.fuelType
                ? `${item.fuelType} ${item.fuelConsumption || ""} ${item.fuelConsumptionUnit || ""}`.trim()
                : item?.energySourceForProcessing || "-";

          const processingEntries = Array.isArray(item?.processingMethodEntries)
            ? item.processingMethodEntries
            : [];
          const materialsDisplay =
            processingEntries.length > 0
              ? processingEntries
                .map((entry) => {
                  const name = entry?.methodName || entry?.processingMethod || "";
                  const qty = entry?.quantity;
                  if (name && qty !== undefined && qty !== null && String(qty).trim() !== "") {
                    return `${name} ${qty} ${entry?.unit || "MT"}`;
                  }
                  return name;
                })
                .filter(Boolean)
                .join(", ")
              : Array.isArray(item?.materialsAddedDuringProcessing)
                ? item.materialsAddedDuringProcessing.join(", ")
                : item?.materialsAddedDuringProcessing || "-";


          return {
            ...item,
            createdAt: formattedCreatedDate,
            createdAtOriginal: originalCreatedDate,
            soldProductDate: item?.soldProductDate || originalCreatedDate,
            productId: item?.productId || item?.productIdSku || "-",
            productName: item?.productMaster?.productName || "-",
            vendorName: item?.customerMaster?.customerVendorName || "-",
            quantitySoldDisplay: quantitySoldValue,
            quantitySold: item?.quantitySold || {},
            // processingMethodUsed: processingMethodDisplay,
            electricityConsumption: electricityVal ?? "",
            energyConsumptionForProcessing: electricityVal ?? "",
            energyDisplay: energyConsumptionValue,
            fuelDisplay,
            materialsAddedDuringProcessing: materialsDisplay,
            action: "View",
            id: item?._id,
          };
        });
        setTableData(arr);
        setTotalPages(res?.data?.totalPages);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    fetchData();
  }, [sortConfig, filterObj, currentPage]);

  const fetchFilters = async () => {
    try {
      const res = await get(`/scope3/processingOfSoldProducts/filter`);
      const { productName, productCategory, vendorName, locationOfProcessing } =
        res?.data?.data || {};
      setFilters({
        productName: productName || [],
        productCategory: productCategory || [],
        vendorName: vendorName || [],
        locationOfProcessing: locationOfProcessing || [],
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchCardData = async () => {
    try {
      const res = await get(`/scope3/processingOfSoldProducts/cardData`);
      const data = res?.data?.data || {};
      setCardData({
        totalProcessings: data.totalProcessings ?? 0,
        topCategory: Array.isArray(data.topCategory) ? data.topCategory : [],
        topVendor: data.topVendor ?? null,
        totalQuantitySold: data.totalQuantitySold ?? 0,
        electricityConsumption: data.electricityConsumption ?? 0,
        totalSoldUnits: Array.isArray(data.totalSoldUnits) ? data.totalSoldUnits : [],
        totalFuel: Array.isArray(data.totalFuel) ? data.totalFuel : [],
        fuelConsumptionSummary: Array.isArray(data.fuelConsumptionSummary)
          ? data.fuelConsumptionSummary
          : [],
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      setIsModalOpen(true);
    } else if (type === "edit") {
      setIsModalOpen(true);
      setEditData(dataForEdit);
      setEditModal(true);
      setViewModal(false);
    }
  };

  const handleEdit = (data) => {
    setIsModalOpen(true);
    setEditData(data);
    setEditModal(true);
    setViewModal(false);
  };

  const openViewModal = (data) => {
    setEditData(data);
    setViewModal(true);
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilterObj) => {
    setFilterObj(newFilterObj);
    setCurrentPage(1);
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setIsModalOpen(false);
      setEditModal(false);
      setEditData({});
    }
  };

  const handleSubmit = async (data, type) => {
    try {
      if (type) {
        let res = await put(
          `/scope3/processingOfSoldProducts/update?Id=${editData._id}`,
          data
        );
        if (res) {
          setsnackOpen({
            open: true,
            message: res.data.message,
            severity: "success",
          });
          setIsModalOpen(false);
          setEditModal(false);
          setEditData({});
          fetchCardData();
        }
      } else {
        let res = await post(`/scope3/processingOfSoldProducts/add`, data);
        if (res) {
          setsnackOpen({
            open: true,
            message: res.data.message,
            severity: "success",
          });
          setIsModalOpen(false);
          fetchCardData();
        }
      }

      fetchData();
    } catch (error) {
      setsnackOpen({
        open: true,
        message: error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });
      console.log("Error in handleSubmit:", error.response?.data?.message);
    }
  };

  const handleSelectChange = (value) => {
    setSelectedValue(value);
  };

  const handleDeleteOpen = (itemId) => {
    setDeletedItemId(itemId);
    setDeleteDialog(true);
  };

  const uploadErrorClose = () => {
    setBulkUploadModal(false);
    setBulkUploadTable(true);
  };

  const handleCloseBulkUpload = () => {
    setBulkUploadModal(false);
    setBulkUploadTable(false);
    setMatchData([]);
    setFormDataBulkUpload([]);
    fetchData();
    fetchCardData();
    fetchExcelData();
  };


  return (
    <>
      <Cards
        data={bannerData}
        title={"Processing of Sold Products"}
        onFilterChange={handleSelectChange}
      />
      <ActionButtons
        onFilterClick={() => setIsFilterModalOpen(true)}
        onAddClick={() => openModal("add")}
        filterObj={filterObj}
        onUploadClick={() => setBulkUploadModal(true)}
      />

      <ViewTable
        title={"Processing of Sold Products"}
        headers={headers}
        data={mapTableDataWithFacility(tableData, showFacilityColumn)}
        onSort={handleSort}
        sortConfig={sortConfig}
        page={currentPage}
        count={pageSize}
        editIcon={user?.companyAdmin ? "disable" : "enable"}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        tableLayoutAuto
        actionColumnWidthPx={72}
        onView={(row) => {
          openViewModal(row);
        }}
        onEdit={(row) => openModal("edit", row)}
        onDelete={(row) => handleDeleteOpen(row)}
      />
      <Snackbar
        open={snackOpen.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setsnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          onClose={() => setsnackOpen({ ...snackOpen, open: false })}
          severity={snackOpen.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message}
        </Alert>
      </Snackbar>

      <Form
        isOpen={isModalOpen}
        onClose={() => closeModal(editModal ? "edit" : "add")}
        onSubmit={handleSubmit}
        header={
          editModal
            ? "Edit Processing of Sold Product"
            : "Add Processing of Sold Product"
        }
        initialData={editData}
        isEditing={editModal}
      />

      <ViewData
        id={editData ? editData._id : ""}
        element={editData}
        title={"Processing of Sold Products"}
        viewData={viewModal}
        setViewData={setViewModal}
        setData={setEditData}
        inputData={inputData}
        handleEdit={handleEdit}
        companyAdmin={user?.companyAdmin}
      />

      {deleteDialog && (
        <DeleteAlertDialog
          {...{
            deleteDialog,
            setDeleteDialog,
            method: "delete",
            id: deletedItemId?._id,
            url: `${api}/scope3/processingOfSoldProducts/delete`,
            setsnackOpen,
            fetchData,
            fetchCardData,
          }}
        />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filterData}
        onFilterChange={handleFilterChange}
        title="Filter"
      />

      {bulkUploadModal && (
        <BulkUploadModal
          onCloseBulkUpload={handleCloseBulkUpload}
          isBulkUploadModal={bulkUploadModal}
          excelData={excelData}
          fileName={"processingOfSoldProducts"}
          setsnackOpen={setsnackOpen}
          url={"/scope3/processingOfSoldProducts/bulkupload"}
          setMatchData={setMatchData}
          setFormDataBulkUpload={setFormDataBulkUpload}
          matchedKey="productAndCustomerStatusList"
          errorClose={uploadErrorClose}
          setBulkUploadModal={setBulkUploadModal}
        />
      )}

      {bulkUploadTable && (
        <BulkUploadReview
          bulkUploadTable={bulkUploadTable}
          matchData={matchData}
          formDataBulkUpload={formDataBulkUpload}
          setFormDataBulkUpload={setFormDataBulkUpload}
          setsnackOpen={setsnackOpen}
          bulkUploadUrl={"/scope3/processingOfSoldProducts/jsonupload"}
          onClose={() => setBulkUploadTable(false)}
          onConfirm={(data) => {
            setBulkUploadTable(false);
            setMatchData([]);
            setFormDataBulkUpload([]);
            fetchData();
            fetchCardData();
            fetchExcelData();

            const skippedEntries = data?.skippedEntries;
            const hasSkipped =
              Array.isArray(skippedEntries) && skippedEntries.length > 0;
            const inserted = Number(data?.inserted ?? 0);
            const skipped = Number(data?.skipped ?? 0);

            if (hasSkipped) {
              setSkippedUploadDialog({
                open: true,
                skippedEntries,
                inserted,
                skipped,
              });
            }
          }}
        />
      )}

      <BulkUploadSkippedDialog
        open={skippedUploadDialog.open}
        skippedEntries={skippedUploadDialog.skippedEntries}
        inserted={skippedUploadDialog.inserted}
        skipped={skippedUploadDialog.skipped}
        downloadFileName="processing_of_sold_products_skipped"
        downloadExcelColumns={PROCESSING_SKIPPED_DOWNLOAD_COLUMNS}
        onClose={() =>
          setSkippedUploadDialog({
            open: false,
            skippedEntries: [],
            inserted: 0,
            skipped: 0,
          })
        }
      />
    </>
  );
};

export default ProcessingOfSoldProducts;
